/**
 * One card in the Agents tab's left rail (task 796).
 *
 * The card answers the three questions that decide whether a User clicks it:
 * who is this agent, is it live, and what model does it actually run on in THIS
 * flow — which is why it resolves the model through `useResolvedLlmConfig`, the
 * same engine-mirroring cascade the Model & runtime panel uses, rather than
 * reading `agent.modelConfig` directly and quietly ignoring node overrides.
 */
import { CpuChipIcon } from '@heroicons/react/24/outline';
import { useAgent } from '@/hooks/useAgents';
import { useResolvedLlmConfig } from './AgentModelPanel';
import type { AgentFlowEntry } from './agentFlow';

interface AgentRailCardProps {
  entry: AgentFlowEntry;
  /** 1-based position in the flow, shown as the card's order marker. */
  position: number;
  selected: boolean;
  onSelect: (agentId: string) => void;
}

export function AgentRailCard({ entry, position, selected, onSelect }: AgentRailCardProps) {
  const { data: agent, isLoading, isError } = useAgent(entry.agentId);
  const resolved = useResolvedLlmConfig(agent, entry.nodes[0]?.llmConfigId);

  // The alias is worth showing only when it differs from the agent's own name —
  // otherwise it is the same word twice.
  const alias = entry.nodes[0]?.alias;
  const showAlias = !!alias && !!agent && alias !== agent.name;

  const name = agent?.name ?? (isError ? 'Agent unavailable' : entry.agentId);
  const model = resolved.config?.model.modelId ?? (resolved.source === 'environment' ? 'Service default' : null);

  return (
    <button
      type="button"
      onClick={() => onSelect(entry.agentId)}
      aria-current={selected ? 'true' : undefined}
      data-testid={`agent-rail-card-${entry.agentId}`}
      data-selected={selected ? 'true' : 'false'}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/30'
          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50'
      } ${isError ? 'border-red-300 dark:border-red-700' : ''}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
        <CpuChipIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              agent?.isActive ? 'bg-green-500' : 'bg-gray-400'
            }`}
          />
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {isLoading ? 'Loading…' : name}
          </span>
        </span>
        {showAlias && (
          <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
            as {alias}
          </span>
        )}
        <span className="mt-0.5 block truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">
          {model ?? '—'}
        </span>
      </span>

      <span className="shrink-0 text-[11px] font-bold text-gray-400 dark:text-gray-500">
        {position}
      </span>
    </button>
  );
}

export default AgentRailCard;
